import { useCallback } from 'react';
import { FieldArray } from 'react-final-form-arrays';

import { IKnmAddressTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmAddressTypeSelectOption';
import { KnmCollapse } from 'KnmPackage/MainModule/components/KnmCollapse';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { KnmAddressField } from 'KnmPackage/MainModule/components/KnmAddressField';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { Button } from 'UIPackage/MainModule/components/Button';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { SelectInputFieldAutoOptions } from 'UIPackage/MainModule/components/SelectInputFieldAutoOptions';
import { IRiskCategorySelectOption } from 'CorePackage/CatalogsModule/interfaces/IRiskCategorySelectOption';

import styles from './KnmErpObject.module.scss';
import { FieldReplaceAlert } from './FieldReplaceAlert';
import { IKnmSupervisionTypeSelectOption } from '../../../CorePackage/CatalogsModule/interfaces/IKnmSupervisionTypeSelectOption';

const COLLAPSE_TITLE_BASE = 'Объект';

interface IKnmErpObjectProps {
  address: string | undefined;
  autocomplete: boolean | undefined;
  formRef: IKnmFormRef | undefined;
  id: string;
  index: number;
  isNew: boolean | undefined;
  knmAddressTypesOptions?: IKnmAddressTypeSelectOption[];
  knmSupervisionTypesSelectOptions: IKnmSupervisionTypeSelectOption[] | undefined;
  name: string;
  onRemove: () => void;
  registryLegalAddressFns?: string;
  replaceAlertsVisible?: boolean;
  riskCategoriesSelectOptions: IRiskCategorySelectOption[];
  watchOnly?: boolean;
}

export const KnmErpObject = ({
  address,
  autocomplete,
  formRef,
  id,
  index,
  isNew,
  knmAddressTypesOptions,
  knmSupervisionTypesSelectOptions,
  name,
  onRemove,
  registryLegalAddressFns,
  replaceAlertsVisible,
  riskCategoriesSelectOptions,
  watchOnly,
}: IKnmErpObjectProps) => {
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;
  const makeAddSupervisionHandler = (fieldName: string) => () => !watchOnly && push && push(fieldName, undefined);
  const makeRemoveSupervisionHandler = (fieldName: string, i: number) => () =>
    !watchOnly && remove && remove(fieldName, i);

  const isAddressFieldAlertVisible = Boolean(
    !isNew && replaceAlertsVisible && registryLegalAddressFns && address !== registryLegalAddressFns,
  );

  const addressFieldName = `${name}.address` as keyof IKnmForm;
  const autocompleteFieldName = `${name}.autocomplete` as keyof IKnmForm;

  const handleAddressFieldBlur = useCallback(
    (addressValue: string) => formRef?.current.change(addressFieldName, addressValue),
    [addressFieldName, formRef],
  );

  const handleAddressReplaceButtonClick = useCallback(() => {
    formRef?.current.change(addressFieldName, registryLegalAddressFns);
  }, [addressFieldName, formRef, registryLegalAddressFns]);

  const handleAutocompleteClear = useCallback(() => {
    formRef?.current.change('organizationAutocomplete', false);
    formRef?.current.change(autocompleteFieldName, false);
  }, [autocompleteFieldName, formRef]);

  return (
    <KnmCollapse
      id={id}
      theme="fields"
      title={isNew ? COLLAPSE_TITLE_BASE : `${COLLAPSE_TITLE_BASE} №${index + 1}`}
      subtitle={address}
      isNew={isNew}
      openByDefault={isNew}
      onClose={onRemove}
    >
      <>
        <Row>
          <FieldBlock label="Местонахождение" placementLabel="left" color="black" labelSize="col-4">
            <KnmAddressField
              resize="none"
              formRef={formRef}
              manualAddressPlaceholder="Адрес"
              id={addressFieldName}
              addressFieldPathWithPrefix={addressFieldName}
              required
              type="row-field"
              withWarning={isAddressFieldAlertVisible}
              clearable={autocomplete}
              disabled={watchOnly || autocomplete}
              onAddressFieldBlur={handleAddressFieldBlur}
              onClear={handleAutocompleteClear}
            />
            {isAddressFieldAlertVisible && (
              <FieldReplaceAlert
                className={styles.FieldReplaceAlert}
                text={`Значение ЕГРЮЛ/ЕГРИП: ${registryLegalAddressFns}`}
                onReplaceButtonClick={handleAddressReplaceButtonClick}
              />
            )}
            <TextareaField className={styles.AddressField} name={addressFieldName} />
          </FieldBlock>
        </Row>

        <Row>
          <FieldBlock label="Тип места" placementLabel="left" color="black" labelSize="col-4">
            <SelectInputField
              className="shared-row-field"
              id={`${name}.addressType`}
              name={`${name}.addressType`}
              options={knmAddressTypesOptions}
              disabled={watchOnly || autocomplete}
              size="medium"
              isClearable
              onClear={autocomplete ? handleAutocompleteClear : undefined}
            />
          </FieldBlock>
        </Row>

        <Row>
          <FieldBlock label="Тип объекта проведения" placementLabel="left" color="black" labelSize="col-4">
            <SelectInputFieldAutoOptions
              className="shared-row-field"
              id={`${name}.objectType`}
              name={`${name}.objectType`}
              optionName={CommonCatalogsNamesEnum.KNM_OBJECT_TYPES}
              disabled={watchOnly}
              size="medium"
            />
          </FieldBlock>
        </Row>

        <Row>
          <FieldBlock label="Категория риска" placementLabel="left" color="black" labelSize="col-4">
            <SelectInputField
              className="shared-row-field"
              id={`${name}.riskCategory`}
              name={`${name}.riskCategory`}
              options={riskCategoriesSelectOptions}
              disabled={watchOnly}
              size="medium"
            />
          </FieldBlock>
        </Row>

        <FieldBlock
          label="Вид (виды) контроля (надзора)"
          button={
            <Button
              className="shared-btn-add"
              text="Добавить"
              variant="link"
              color="primary"
              type="button"
              disabled={watchOnly}
              onClick={makeAddSupervisionHandler(`${name}.supervisions`)}
            />
          }
        >
          <FieldArray name={`${name}.supervisions`}>
            {({ fields }) => {
              if (!fields.length) {
                return null;
              }

              return fields.map((supervisionsFieldName, sIndex) => (
                <Row key={supervisionsFieldName}>
                  <SelectInputField
                    className="shared-deleting-row"
                    name={supervisionsFieldName}
                    options={knmSupervisionTypesSelectOptions}
                    isSearchable
                    disabled={watchOnly}
                    size="medium"
                  />
                  <CloseButton
                    className="shared-deleting-row-btn"
                    onClick={makeRemoveSupervisionHandler(`${name}.supervisions`, sIndex)}
                  />
                </Row>
              ));
            }}
          </FieldArray>
        </FieldBlock>
      </>
    </KnmCollapse>
  );
};
